import { Button } from '@workspace/ui/components/button';
import { ArrowRight, Globe } from 'lucide-react';
import { useState } from 'react';
import { m } from '~/paraglide/messages.js';
import type { WhereAreYouStartData } from '../-schemas/org-schema';

type Props = {
  values: WhereAreYouStartData;
  onUpdate: (data: WhereAreYouStartData) => void;
  onNext: () => void;
};

type CountryOption = {
  id: string;
  name: string;
  description: string;
  flag: string;
  estimatedTime: string;
  timeLabel: string;
};

const COUNTRY_OPTIONS: CountryOption[] = [
  {
    id: 'colombia',
    name: m['organization.where_start.colombia_name'](),
    description: m['organization.where_start.colombia_description'](),
    flag: '/images/flags/flag-co.png',
    estimatedTime: m['organization.where_start.colombia_time'](),
    timeLabel: m['organization.where_start.company_creation_label'](),
  },
  {
    id: 'usa',
    name: m['organization.where_start.usa_name'](),
    description: m['organization.where_start.usa_description'](),
    flag: '/images/flags/flag-us.png',
    estimatedTime: m['organization.where_start.usa_time'](),
    timeLabel: m['organization.where_start.company_creation_label'](),
  },
  {
    id: 'global',
    name: m['organization.where_start.global_name'](),
    description: m['organization.where_start.option_dao'](),
    flag: '',
    estimatedTime: m['organization.where_start.global_time'](),
    timeLabel: m['organization.where_start.company_creation_label'](),
  },
];

type CountryCardProps = {
  option: CountryOption;
  isSelected: boolean;
  onClick: () => void;
};

function CountryCard({ option, isSelected, onClick }: CountryCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex w-full items-center gap-4 rounded-xl border p-4 text-left transition-all hover:bg-gray-900/50 focus:outline-none focus:ring-2 ${
        isSelected
          ? 'border-[#e4e4e7] bg-[#e4e4e7]/10'
          : 'border-gray-800 hover:border-gray-700'
      }`}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-900">
        {option.flag ? (
          <img src={option.flag} alt="" />
        ) : (
          <Globe className="h-6 w-6 text-gray-400" />
        )}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-white">{option.name}</h3>
        <p className="text-sm text-gray-400">{option.description}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-gray-500">{option.timeLabel}</p>
        <p className="text-sm font-medium text-white">{option.estimatedTime}</p>
      </div>
    </button>
  );
}

export function WhereAreYouStartComponent({ values, onUpdate, onNext }: Props) {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    values.name || null,
  );

  const handleContinue = () => {
    if (selectedCountry) {
      onUpdate({ name: selectedCountry });
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-400 text-center">
        {m['organization.where_start.registration_time_message']()}
      </p>

      <div className="space-y-3">
        {COUNTRY_OPTIONS.map((option) => (
          <CountryCard
            key={option.id}
            option={option}
            isSelected={selectedCountry === option.id}
            onClick={() => setSelectedCountry(option.id)}
          />
        ))}
      </div>

      <div className="space-y-3">
        <p className="text-center text-xs text-gray-500">
          {m['organization.where_start.more_countries_soon']()}
        </p>

        <Button
          onClick={handleContinue}
          disabled={!selectedCountry}
          className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-6 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
        >
          {m['common.continue']()}
          <ArrowRight className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}
